<template>
  <div
    class="login-page"
    :class="{ 'c-dark-theme': $store.state.darkMode }"
  >
    <ToastContainer :toasts="toasts" />

    <div class="login-split">
      <!-- Brand / value panel (desktop) -->
      <aside class="login-brand d-none d-lg-flex">
        <div class="brand-inner">
          <router-link to="/" class="logo-row" style="text-decoration: none; color: inherit;">
            <span class="logo-mark">N</span>
            <span class="logo-word">Niagaku</span>
          </router-link>

          <h1 class="brand-title">Everything your business needs, in one place.</h1>
          <p class="brand-sub">
            Invoicing, accounting, payroll, and inventory — built for Malaysian SMEs.
          </p>

          <ul class="brand-features">
            <li class="brand-feature">
              <span class="feature-check">&#10003;</span>
              Invoices, quotations &amp; receipts in seconds
            </li>
            <li class="brand-feature">
              <span class="feature-check">&#10003;</span>
              Payroll with EPF, SOCSO &amp; EIS handled for you
            </li>
            <li class="brand-feature">
              <span class="feature-check">&#10003;</span>
              Real-time accounting &amp; inventory
            </li>
          </ul>

          <div class="brand-trust">
            <CIcon name="cil-shield-alt" size="sm" class="mr-1"/>
            Trusted by 500+ Malaysian businesses &middot; Bank-grade security
          </div>
        </div>
      </aside>

      <!-- Form panel -->
      <main class="login-form-panel">
        <div class="login-card fade-in-up">
          <!-- Mobile logo -->
          <div class="mobile-logo d-lg-none">
            <span class="logo-mark">N</span>
            <span class="logo-word logo-word--navy">Niagaku</span>
          </div>

          <h2 class="form-title">Welcome back</h2>
          <p class="form-sub">Sign in to manage your business.</p>

          <CForm @submit.prevent="login">
            <!-- Email Field -->
            <div class="field">
              <label class="field-label">Email address</label>
              <CInput
                placeholder="name@company.com"
                autocomplete="username email"
                v-model="loginObj.email"
                :invalid="!isValidEmail && loginObj.email.length > 0"
                @input="validateEmail"
                aria-label="Email address"
                add-input-classes="login-input"
                data-test-id="usernameInput"
              >
                <template #prepend-content>
                  <CIcon name="cil-user"/>
                </template>
              </CInput>
              <small
                v-if="!isValidEmail && loginObj.email.length > 0"
                class="text-danger d-block mt-1"
              >
                Please enter a valid email address.
              </small>
            </div>

            <!-- Password Field -->
            <div class="field">
              <label class="field-label">Password</label>
              <CInput
                placeholder="Enter your password"
                type="password"
                autocomplete="current-password"
                v-model="loginObj.password"
                :invalid="!isValidPassword && loginObj.password.length > 0"
                @input="validatePassword"
                aria-label="Password"
                add-input-classes="login-input"
                data-test-id="passwordInput"
              >
                <template #prepend-content>
                  <CIcon name="cil-lock-locked"/>
                </template>
              </CInput>
              <small
                v-if="!isValidPassword && loginObj.password.length > 0"
                class="text-danger d-block mt-1"
              >
                Password is required.
              </small>
            </div>

            <!-- Forgot Password Link -->
            <div class="text-right mb-3">
              <CButton
                color="link"
                class="px-0 forgot-link"
                @click="navigateTo('/forgotPassword')"
              >
                Forgot password?
              </CButton>
            </div>

            <!-- Login Button -->
            <CButton
              type="submit"
              :disabled="loading"
              block
              class="font-weight-semibold btn-marketing-accent login-submit"
              data-test-id="loginButton"
            >
              <CSpinner v-if="loading" size="sm" class="mr-2" />
              {{ loading ? 'Signing in…' : 'Sign In' }}
            </CButton>

            <!-- Google SSO Divider -->
            <div class="divider">
              <span class="divider-line"></span>
              <span class="divider-text">or continue with</span>
              <span class="divider-line"></span>
            </div>

            <!-- Google Sign-In Button -->
            <div ref="googleButton" class="d-flex justify-content-center mb-2"></div>

            <!-- Register -->
            <p class="register-line">
              New to Niagaku?
              <CButton color="link" class="register-link px-1" @click="navigateTo('/register')">
                Create a free account
              </CButton>
            </p>
          </CForm>

          <!-- Back to Home -->
          <div class="text-center mt-3">
            <CButton
              color="link"
              class="home-link"
              @click="navigateTo('/')"
            >
              <CIcon name="cil-arrow-left" class="mr-1" size="sm"/>
              Back to homepage
            </CButton>
          </div>

          <!-- Trust signals (mobile only — desktop shows them in the brand panel) -->
          <div class="mobile-trust d-lg-none">
            <span><CIcon name="cil-shield-alt" class="mr-1" size="sm"/>Bank-Grade Security</span>
            <span><CIcon name="cil-lock-locked" class="mr-1" size="sm"/>SSL Encrypted</span>
            <span><CIcon name="cil-people" class="mr-1" size="sm"/>500+ Businesses</span>
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex"; // Import Vuex helpers
import ToastContainer from "../widgets/ToastContainer.vue";
import ChatbotAiApi from '../../lib/chatbotAiApi'; // adjust path as needed

const chatbotApi = new ChatbotAiApi();

export default {
  name: "Login",
  components: {
    ToastContainer,
  },
  data: () => {
    return {
      toasts: [],
      loginObj: {
        email: "",
        password: "",
      },
      loading: false,
      isValidEmail: true,
      isValidPassword: true,
    };
  },
  computed: {
    ...mapState({
      // logoUrl: (state) => state.logoUrl, // Access the logo from Vuex
      logoUrl: (state) => state.logoUrl || "/logo.png", // Access the logo from Vuex
      primaryColor: (state) => state.primaryColor || "#756CFB", // Access the primary color from Vuex
    }),
  },
  mounted() {
    this.initGoogleSignIn();
  },
  methods: {
    initGoogleSignIn() {
      const self = this;
      const checkGoogle = setInterval(() => {
        if (window.google && window.google.accounts) {
          clearInterval(checkGoogle);
          window.google.accounts.id.initialize({
            client_id: window.config.GOOGLE_CLIENT_ID,
            callback: self.handleGoogleResponse,
          });
          window.google.accounts.id.renderButton(
            self.$refs.googleButton,
            { theme: 'outline', size: 'large', width: 360, text: 'signin_with' }
          );
        }
      }, 100);
      // Stop checking after 10 seconds
      setTimeout(() => clearInterval(checkGoogle), 10000);
    },
    handleGoogleResponse(response) {
      this.loading = true;
      auth
        .doGoogleLogin({ idToken: response.credential })
        .then((result) => {
          this.loading = false;
          auth.recordLogin(result.accessToken, result, false);
          this.$router.push({ path: this.afterLoginPath() });
        })
        .catch(({ data }) => {
          this.toast("Error", data || "Google sign-in failed", "danger");
          this.loading = false;
        });
    },
    login() {
      if (!this.isValidEmail || !this.isValidPassword) {
        this.toast(
          "Error",
          "Please fix the errors before submitting.",
          "danger"
        );
        return;
      }

      this.loading = true;
      let data = {
        email: this.loginObj.email,
        password: this.loginObj.password,
      };
      auth
        .doLogin(data)
        .then((response) => {
          this.loading = false;
          auth.recordLogin(response.accessToken, response, false);

          // Extract businessId and profileId from user data
          const user = response.userData; // or response.user if wrapped
          const businessId = user.profile.defaultBusinessId;
          const profileId = user.profileId;

          // // Store user in localStorage so ChatbotWidget can use it
          // window.localStorage.setItem("user", JSON.stringify(user));

          // Call chatbot /refresh
          chatbotApi
            .refresh(businessId, profileId)
            .then(() => {})
            .catch((err) => {
              console.warn("[Chatbot] Refresh failed:", err);
            });

          this.$router.push({ path: this.afterLoginPath() });
        })
        .catch(({ data }) => {
          this.toast("Error", data || "Login failed", "danger");
          this.loading = false;
        });
    },
    // Return to the page that sent the user to sign in (e.g. the AI connection consent page).
    // Same-site paths only, so ?redirect= can't send users to another website.
    afterLoginPath() {
      const redirect = this.$route.query.redirect;
      const isLocalPath = typeof redirect === "string" && redirect.startsWith("/") && !redirect.startsWith("//") && !redirect.includes("\\");
      return isLocalPath ? redirect : "/dashboard";
    },
    navigateTo(path) {
      this.$router.push({ path: path });
    },
    toast(header, message, color) {
      this.toasts.push({
        header: header,
        message: message,
        color: color,
      });
    },
    validateEmail() {
      const regex = /^\S+@\S+\.\S+$/;
      this.isValidEmail = regex.test(this.loginObj.email);
    },
    validatePassword() {
      this.isValidPassword = this.loginObj.password.length >= 1;
    },
  },
};
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  background: var(--marketing-gray-50);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.login-split {
  display: flex;
  min-height: 100vh;
}

/* ---------- Brand panel ---------- */
.login-brand {
  position: relative;
  flex: 1 1 50%;
  overflow: hidden;
  padding: 4rem 4.5rem;
  color: #fff;
  background:
    radial-gradient(circle at 80% 12%, rgba(16, 185, 129, 0.22) 0%, transparent 44%),
    linear-gradient(160deg, var(--marketing-primary-dark) 0%, var(--marketing-primary) 58%, var(--marketing-primary-light) 100%);
}

/* Faint dot grid, faded toward the bottom-right */
.login-brand::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image: radial-gradient(rgba(255, 255, 255, 0.09) 1px, transparent 1px);
  background-size: 22px 22px;
  -webkit-mask-image: linear-gradient(160deg, rgba(0, 0, 0, 0.7), transparent 72%);
  mask-image: linear-gradient(160deg, rgba(0, 0, 0, 0.7), transparent 72%);
  pointer-events: none;
}

.brand-inner {
  position: relative;
  z-index: 1;
  margin: auto 0;
  max-width: 440px;
}

.logo-row {
  display: inline-flex;
  align-items: center;
  gap: 0.7rem;
}

.logo-mark {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-weight: 800;
  font-size: 1.2rem;
  color: #fff;
  background: linear-gradient(135deg, var(--marketing-accent) 0%, var(--marketing-accent-dark) 100%);
  box-shadow: 0 6px 18px rgba(16, 185, 129, 0.35);
}

.logo-word {
  font-size: 1.25rem;
  font-weight: 700;
  letter-spacing: -0.01em;
}

.logo-word--navy {
  color: var(--marketing-primary);
}

.brand-title {
  font-size: clamp(1.9rem, 2.6vw, 2.55rem);
  font-weight: 800;
  line-height: 1.14;
  letter-spacing: -0.025em;
  margin: 2.75rem 0 1rem;
  text-wrap: balance;
}

.brand-sub {
  font-size: 1.05rem;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.78);
  margin-bottom: 2.25rem;
  max-width: 38ch;
}

.brand-features {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.brand-feature {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  font-size: 0.97rem;
  color: rgba(255, 255, 255, 0.92);
}

.feature-check {
  flex: 0 0 auto;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-size: 0.8rem;
  color: #fff;
  background: rgba(16, 185, 129, 0.92);
}

.brand-trust {
  display: flex;
  align-items: center;
  margin-top: 3rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.14);
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.66);
}

/* ---------- Form panel ---------- */
.login-form-panel {
  flex: 1 1 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1.5rem;
  background: #fff;
}

.login-card {
  width: 100%;
  max-width: 400px;
}

.mobile-logo {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.7rem;
  margin-bottom: 2rem;
}

.form-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: var(--marketing-primary);
  letter-spacing: -0.02em;
  margin-bottom: 0.35rem;
}

.form-sub {
  color: var(--marketing-gray-500);
  margin-bottom: 2rem;
}

.field {
  margin-bottom: 1.1rem;
}

.field-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--marketing-gray-700);
  margin-bottom: 0.4rem;
}

.forgot-link,
.register-link {
  color: var(--marketing-accent) !important;
  font-size: 0.875rem;
  font-weight: 600;
  text-decoration: none;
}
.forgot-link:hover,
.register-link:hover {
  color: var(--marketing-accent-dark) !important;
  text-decoration: underline;
}

.login-submit {
  border-radius: 10px;
  padding: 0.7rem;
  font-size: 1rem;
  /* Press feedback — confirms the interface heard the user. */
  transition: transform 160ms cubic-bezier(0.23, 1, 0.32, 1);
}
.login-submit:active {
  transform: scale(0.985);
}

.divider {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  margin: 1.5rem 0;
}
.divider-line {
  flex: 1 1 auto;
  height: 1px;
  background: var(--marketing-gray-200);
}
.divider-text {
  font-size: 0.8rem;
  color: var(--marketing-gray-400);
}

.register-line {
  text-align: center;
  font-size: 0.9rem;
  color: var(--marketing-gray-500);
  margin: 0.25rem 0 0;
}

.home-link {
  color: var(--marketing-gray-500) !important;
  font-size: 0.85rem;
  text-decoration: none;
}
.home-link:hover {
  color: var(--marketing-gray-700) !important;
}

.mobile-trust {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1.25rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid var(--marketing-gray-200);
}
.mobile-trust span {
  display: inline-flex;
  align-items: center;
  font-size: 0.72rem;
  color: var(--marketing-gray-500);
}
.mobile-trust .c-icon {
  color: var(--marketing-accent);
}

/* Card entrance — fires on mount, enhances an already-visible default. */
.fade-in-up {
  animation: fade-in-up 500ms cubic-bezier(0.23, 1, 0.32, 1) both;
}
@keyframes fade-in-up {
  from { opacity: 0; transform: translateY(14px); }
  to { opacity: 1; transform: none; }
}

/* ---------- Inputs (CoreUI internals via deep) ---------- */
::v-deep .input-group-text {
  background: #fff;
  border: 1px solid var(--marketing-gray-300);
  border-right: none;
  border-top-left-radius: 10px;
  border-bottom-left-radius: 10px;
  color: var(--marketing-gray-400);
  padding-left: 0.9rem;
}

::v-deep .login-input {
  border: 1px solid var(--marketing-gray-300);
  border-left: none;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  padding: 0.7rem 0.9rem;
  height: auto;
  font-size: 0.95rem;
  transition: border-color 150ms ease;
}
::v-deep .login-input::placeholder {
  color: var(--marketing-gray-400);
}

/* Emerald focus ring on the whole group */
::v-deep .input-group:focus-within {
  border-radius: 10px;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.16);
}
::v-deep .input-group:focus-within .input-group-text,
::v-deep .input-group:focus-within .login-input {
  border-color: var(--marketing-accent);
}
::v-deep .login-input:focus {
  box-shadow: none;
  outline: none;
}

/* Invalid state */
::v-deep .login-input.is-invalid {
  border-color: var(--marketing-danger);
  background-image: none;
}

/* ---------- Responsive ---------- */
@media (max-width: 991.98px) {
  .login-form-panel {
    padding: 2rem 1.25rem;
    min-height: 100vh;
  }
}

@media (prefers-reduced-motion: reduce) {
  .fade-in-up { animation: none; }
  .login-submit { transition: none; }
  .login-submit:active { transform: none; }
}
</style>
